import * as Sentry from "@sentry/react";
import { getCurrentEnv } from "~/common/utils";
import type { AuthUserGroup } from "~/types/auth";
import type { MetricAttributes, MetricDef } from "./catalog";

/**
 * Emitter for the UX metric catalog.
 *
 * Sentry is the sink today, but every call site goes through `analytics.*`
 * rather than `Sentry.metrics.*` directly. The catalog plus this indirection
 * are the durable part: swapping or adding a sink later is one file, not a
 * few hundred call sites.
 *
 * Metrics inherit Sentry's `enabled` flag, so they are inert in dev. To see
 * them locally without a DSN, set `localStorage["portal.analytics.debug"] =
 * "1"` and they log to the console instead.
 */

type AttributeValue = string | number | boolean;

/**
 * Longest string an attribute value may be. Enum values, bucket labels and
 * route literals are far under this; free text (a note fragment, a patient
 * name, an OCR snippet) is not. Bounding the length is a cheap backstop
 * against a call site passing user-authored content by mistake — the type
 * system cannot tell one `string` from another.
 */
const MAX_ATTRIBUTE_LENGTH = 64;

const DEBUG_FLAG_KEY = "portal.analytics.debug";

const isDebugEnabled = (): boolean => {
  try {
    return localStorage.getItem(DEBUG_FLAG_KEY) === "1";
  } catch {
    // Private mode / blocked storage — treat as off.
    return false;
  }
};

/** Loud in dev and stage, silent in prod: a dropped attribute is a bug, not a user-facing failure. */
const warnDropped = (metric: string, key: string, reason: string): void => {
  if (getCurrentEnv() === "prod") return;
  console.warn(
    `[analytics] dropped attribute "${key}" on "${metric}": ${reason}`,
  );
};

type SanitizeResult =
  | { ok: true; value: AttributeValue }
  | { ok: false; reason: string };

/**
 * Every value bound for Sentry passes through here — metric attributes and
 * shared attributes alike. Types cannot constrain what a `string` holds, so
 * this is the one place that decides what is safe to send.
 */
const sanitizeValue = (value: unknown): SanitizeResult => {
  if (typeof value === "boolean") return { ok: true, value };
  if (typeof value === "number") {
    return Number.isFinite(value)
      ? { ok: true, value }
      : { ok: false, reason: "non-finite number" };
  }
  if (typeof value === "string") {
    return value.length <= MAX_ATTRIBUTE_LENGTH
      ? { ok: true, value }
      : {
          ok: false,
          reason: `string longer than ${MAX_ATTRIBUTE_LENGTH} chars`,
        };
  }
  return { ok: false, reason: `unsupported type "${typeof value}"` };
};

/**
 * Keep only declared keys holding bounded primitives. The type system already
 * catches undeclared keys at compile time; this is the runtime backstop for
 * untyped call sites.
 */
const sanitizeAttributes = (
  def: MetricDef,
  attrs: Record<string, unknown> | undefined,
): Record<string, AttributeValue> => {
  const clean: Record<string, AttributeValue> = {};
  if (!attrs) return clean;

  for (const [key, value] of Object.entries(attrs)) {
    if (!def.attrs.includes(key)) {
      warnDropped(def.name, key, "not declared in the catalog");
      continue;
    }
    const result = sanitizeValue(value);
    if (result.ok) {
      clean[key] = result.value;
    } else {
      warnDropped(def.name, key, result.reason);
    }
  }
  return clean;
};

const emit = (
  def: MetricDef,
  value: number,
  attrs: Record<string, unknown> | undefined,
): void => {
  const attributes = sanitizeAttributes(def, attrs);
  const options = def.unit ? { unit: def.unit, attributes } : { attributes };

  if (isDebugEnabled()) {
    console.debug(`[analytics] ${def.kind} ${def.name} = ${value}`, attributes);
  }

  switch (def.kind) {
    case "count":
      Sentry.metrics.count(def.name, value, options);
      break;
    case "gauge":
      Sentry.metrics.gauge(def.name, value, options);
      break;
    case "distribution":
      Sentry.metrics.distribution(def.name, value, options);
      break;
  }
};

export const analytics = {
  /** Increment a counter. Defaults to 1 — the common case is "this happened". */
  count<D extends MetricDef<"count">>(
    def: D,
    attrs?: MetricAttributes<D>,
    value = 1,
  ): void {
    emit(def, value, attrs);
  },

  /** Record a point-in-time value. */
  gauge<D extends MetricDef<"gauge">>(
    def: D,
    value: number,
    attrs?: MetricAttributes<D>,
  ): void {
    emit(def, value, attrs);
  },

  /** Record one observation in a distribution (percentiles come from these). */
  distribution<D extends MetricDef<"distribution">>(
    def: D,
    value: number,
    attrs?: MetricAttributes<D>,
  ): void {
    emit(def, value, attrs);
  },

  /**
   * Time a synchronous call and record it as a distribution. Keeps the
   * `performance.now()` bookkeeping (and the millisecond unit) in one place
   * rather than at each call site.
   */
  measure<D extends MetricDef<"distribution">, T>(
    def: D,
    fn: () => T,
    attrs?: MetricAttributes<D>,
  ): T {
    const start = performance.now();
    try {
      return fn();
    } finally {
      emit(def, performance.now() - start, attrs);
    }
  },

  /**
   * Shared attribute applied to every subsequent metric. Role is the primary
   * breakdown dimension for adoption questions, and `Sentry.setUser` does not
   * reliably carry a custom field onto metrics — so set it explicitly.
   *
   * Narrowed to the auth enum, and still routed through `sanitizeValue`, so
   * this path carries the same guarantee as the metric emitters rather than
   * being a way around them.
   */
  setUserRole(role: AuthUserGroup): void {
    const result = sanitizeValue(role);
    if (!result.ok) {
      warnDropped("shared attributes", "user_role", result.reason);
      return;
    }
    Sentry.setAttributes({ user_role: result.value });
  },
};
